// src/services/aiAnalytics.ts
// Unified AI analytics for web surface.
//
// Canonical event names shared across Web, Mobile, and Desktop.
// Fire-and-forget — never blocks UI or throws.
// v1: console-based structured logging.

// ============================================================
// Canonical Event Names (shared across all platforms)
// ============================================================

export type AIAnalyticsEvent =
  // Quick answer
  | 'ai.quick_answer.triggered'
  | 'ai.quick_answer.success'
  | 'ai.quick_answer.failure'
  // Suggested actions (from any AI feature)
  | 'ai.suggested_action.clicked'
  // Digest
  | 'ai.digest.shown'
  | 'ai.digest.hidden'
  | 'ai.digest.failure'
  // Thread AI
  | 'ai.thread.summary.triggered'
  | 'ai.thread.summary.success'
  | 'ai.thread.summary.failure'
  | 'ai.thread.reply_suggestion.triggered'
  | 'ai.thread.reply_suggestion.clicked'
  | 'ai.thread.reply_suggestion.failure'
  // Full assistant
  | 'ai.assistant.launched'
  | 'ai.assistant.meeting_launched'
  // Fallback
  | 'ai.fallback_to_manual';

// ============================================================
// Standardized Payload
// ============================================================

export interface AIAnalyticsPayload {
  event: AIAnalyticsEvent;
  surface: 'web' | 'desktop' | 'mobile';
  feature: string;
  entityType?: string;
  entityId?: string;
  success?: boolean;
  timeToValueMs?: number;
  // Additional context
  question?: string;
  actionType?: string;
  errorCode?: string;
  reason?: string;
  [key: string]: unknown;
}

// ============================================================
// Core
// ============================================================

export function trackAIEvent(payload: AIAnalyticsPayload): void {
  try {
    if (process.env.NODE_ENV === 'development') {
      console.log('[AI Analytics]', payload.event, payload);
    }
  } catch {
    // Swallow — analytics must never break the app
  }
}

// ============================================================
// Quick Answer
// ============================================================

export function trackQuickAnswerTriggered(scope: string, entityId: string, question: string): void {
  trackAIEvent({ event: 'ai.quick_answer.triggered', surface: 'web', feature: 'quick_answer', entityType: scope, entityId, question });
}

export function trackQuickAnswerSuccess(scope: string, entityId: string, question: string, timeToValueMs: number): void {
  trackAIEvent({ event: 'ai.quick_answer.success', surface: 'web', feature: 'quick_answer', entityType: scope, entityId, question, success: true, timeToValueMs });
}

export function trackQuickAnswerFailure(scope: string, entityId: string, question: string, errorCode: string): void {
  trackAIEvent({ event: 'ai.quick_answer.failure', surface: 'web', feature: 'quick_answer', entityType: scope, entityId, question, success: false, errorCode });
}

// ============================================================
// Suggested Action (canonical — replaces action_clicked/action_tapped)
// ============================================================

export function trackSuggestedActionClicked(feature: string, entityType: string, entityId: string, actionType: string): void {
  trackAIEvent({ event: 'ai.suggested_action.clicked', surface: 'web', feature, entityType, entityId, actionType });
}

// ============================================================
// Digest
// ============================================================

export function trackDigestShown(timeToValueMs?: number): void {
  trackAIEvent({ event: 'ai.digest.shown', surface: 'web', feature: 'digest', success: true, timeToValueMs });
}

export function trackDigestHidden(reason: string): void {
  trackAIEvent({ event: 'ai.digest.hidden', surface: 'web', feature: 'digest', reason });
}

export function trackDigestFailure(errorCode: string): void {
  trackAIEvent({ event: 'ai.digest.failure', surface: 'web', feature: 'digest', success: false, errorCode });
}

// ============================================================
// Full Assistant
// ============================================================

export function trackAssistantLaunched(entityType: string, entityId: string, context: string): void {
  trackAIEvent({ event: 'ai.assistant.launched', surface: 'web', feature: 'assistant', entityType, entityId, reason: context });
}

export function trackAssistantMeetingLaunched(entityType: string, entityId: string): void {
  trackAIEvent({ event: 'ai.assistant.meeting_launched', surface: 'web', feature: 'assistant', entityType, entityId });
}

// ============================================================
// Fallback to Manual
// ============================================================

let _lastAIFailure: { feature: string; entityType?: string; entityId?: string; timestamp: number } | null = null;
const FALLBACK_WINDOW_MS = 10_000; // 10 seconds

/**
 * Record that an AI feature just failed. Call this from failure handlers.
 * If the user takes a related manual action within 10s, trackManualAction() fires the signal.
 */
export function recordAIFailure(feature: string, entityType?: string, entityId?: string): void {
  _lastAIFailure = { feature, entityType, entityId, timestamp: Date.now() };
}

/**
 * Call this when the user takes a manual action that could be a fallback from AI.
 * If an AI failure was recorded within the last 10 seconds, fires ai.fallback_to_manual.
 */
export function trackManualAction(entityType?: string, entityId?: string): void {
  if (!_lastAIFailure) return;
  const elapsed = Date.now() - _lastAIFailure.timestamp;
  if (elapsed > FALLBACK_WINDOW_MS) {
    _lastAIFailure = null;
    return;
  }
  trackAIEvent({
    event: 'ai.fallback_to_manual',
    surface: 'web',
    feature: _lastAIFailure.feature,
    entityType: entityType || _lastAIFailure.entityType,
    entityId: entityId || _lastAIFailure.entityId,
    timeToValueMs: elapsed,
  });
  _lastAIFailure = null;
}
